import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

export type NotificationStreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface NotificationState {
  unreadCount: number;
  streamStatus: NotificationStreamStatus;
  announcement: string;
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  decrementUnread: (by?: number) => void;
  announce: (message: string) => void;
  setStreamStatus: (status: NotificationStreamStatus) => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      unreadCount: 0,
      streamStatus: 'idle',
      announcement: '',
      setUnreadCount: (count) =>
        set({ unreadCount: Math.max(0, count) }, false, 'notifications/setUnreadCount'),
      incrementUnread: () =>
        set({ unreadCount: get().unreadCount + 1 }, false, 'notifications/incrementUnread'),
      decrementUnread: (by = 1) =>
        set(
          { unreadCount: Math.max(0, get().unreadCount - by) },
          false,
          'notifications/decrementUnread',
        ),
      announce: (message) => {
        if (!message.trim()) return;
        set({ announcement: message }, false, 'notifications/announce');
      },
      setStreamStatus: (status) => {
        if (get().streamStatus === status) return;
        set({ streamStatus: status }, false, 'notifications/setStreamStatus');
      },
    }),
    { name: 'NotificationStore', enabled: import.meta.env.DEV },
  ),
);

export const useNotificationUnreadCount = () => useNotificationStore((s) => s.unreadCount);
export const useNotificationAnnouncement = () => useNotificationStore((s) => s.announcement);
export const useNotificationStreamConnected = () =>
  useNotificationStore((s) => s.streamStatus === 'open');
export const useNotificationActions = () =>
  useNotificationStore(
    useShallow((s) => ({
      setUnreadCount: s.setUnreadCount,
      incrementUnread: s.incrementUnread,
      decrementUnread: s.decrementUnread,
      announce: s.announce,
      setStreamStatus: s.setStreamStatus,
    })),
  );
